import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ToastContainer } from './components/feedback/ToastContainer';
import { PageTransition } from './components/navigation/PageTransition';
import { useAuthStore } from './store/auth';
import { DarkModeProvider } from './context/DarkModeContext';
import { ScrollToTop } from './components/navigation/ScrollToTop';
import { useLocation } from 'react-router-dom';

const HomePage = React.lazy(() =>
  import('./pages/HomePage').then((module) => ({ default: module.HomePage }))
);
const ArticlePage = React.lazy(() =>
  import('./pages/ArticlePage').then((module) => ({ default: module.ArticlePage }))
);
const LoginPage = React.lazy(() =>
  import('./pages/LoginPage').then((module) => ({ default: module.LoginPage }))
);
const RegisterPage = React.lazy(() =>
  import('./pages/RegisterPage').then((module) => ({ default: module.RegisterPage }))
);
const AdminPage = React.lazy(() =>
  import('./pages/AdminPage').then((module) => ({ default: module.AdminPage }))
);
const UserProfile = React.lazy(() =>
  import('./pages/UserProfile').then((module) => ({ default: module.UserProfile }))
);
const TermsPage = React.lazy(() =>
  import('./pages/TermsPage').then((module) => ({ default: module.TermsPage }))
);
const PrivacyPage = React.lazy(() =>
  import('./pages/PrivacyPage').then((module) => ({ default: module.PrivacyPage }))
);
const AboutPage = React.lazy(() =>
  import('./pages/AboutPage').then((module) => ({ default: module.AboutPage }))
);
const ContactPage = React.lazy(() =>
  import('./pages/ContactPage').then((module) => ({ default: module.ContactPage }))
);
const PDFDownloadPage = React.lazy(() =>
  import('./pages/PDFDownloadPage').then((module) => ({ default: module.PDFDownloadPage }))
);
const OpinionsPage = React.lazy(() =>
  import('./pages/OpinionsPage').then((module) => ({ default: module.OpinionsPage }))
);
const OpinionDetailPage = React.lazy(() =>
  import('./pages/OpinionDetailPage').then((module) => ({ default: module.OpinionDetailPage }))
);
const WriterDashboardPage = React.lazy(() =>
  import('./pages/WriterDashboardPage').then((module) => ({ default: module.WriterDashboardPage }))
);

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || !['admin', 'editor'].includes(user?.role || '')) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

function ProtectedWriterRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || user?.role !== 'writer') {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

function AppContent() {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <Header />
      <ToastContainer />
      <main className="pt-24 md:pt-28">
        <AnimatePresence mode="wait" initial={false}>
          <PageTransition key={location.pathname}>
            <Suspense fallback={<div className="min-h-[60vh]" />}>
              <Routes location={location}>
                <Route path="/" element={<HomePage />} />
                <Route path="/article/:slug" element={<ArticlePage />} />
                <Route path="/user/:userId" element={<UserProfile />} />
                <Route path="/pdf-download" element={<PDFDownloadPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/opiniao" element={<OpinionsPage />} />
                <Route path="/opiniao/:slug" element={<OpinionDetailPage />} />
                <Route
                  path="/writer"
                  element={
                    <ProtectedWriterRoute>
                      <WriterDashboardPage />
                    </ProtectedWriterRoute>
                  }
                />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </PageTransition>
        </AnimatePresence>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </QueryClientProvider>
    </DarkModeProvider>
  );
}

export default App;
